import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiAlertCircle } from 'react-icons/fi';

const LOGIN_ERROR_KEY = 'login_error_message';

const Login = () => {
  console.log('[DEBUG] Renderizando componente Login');
  const { register, handleSubmit, formState: { errors } } = useForm();
  const { login } = useAuth();
  const navigate = useNavigate();
  
  // Verificar se há uma mensagem de erro no localStorage
  const savedError = localStorage.getItem(LOGIN_ERROR_KEY);
  console.log('[DEBUG] Erro salvo no localStorage:', savedError);
  
  const [loginError, setLoginError] = useState(savedError || null);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(!!savedError);
  
  // Limpar o erro do localStorage quando o componente for montado
  useEffect(() => {
    console.log('[DEBUG] Componente Login montado, erro atual:', loginError);
    
    // Se tiver um erro salvo, mostrar e depois limpar do localStorage
    if (savedError) {
      setShowError(true);
      setLoginError(savedError);
      // Limpar depois de um curto período para evitar problemas de flash
      setTimeout(() => {
        localStorage.removeItem(LOGIN_ERROR_KEY);
      }, 500);
    }
    
    return () => {
      console.log('[DEBUG] Componente Login desmontado');
    };
  }, []);

  const onSubmit = async (data) => {
    console.log('[DEBUG] Iniciando login...');
    setIsLoading(true);
    setShowError(false);
    setLoginError(null);
    
    // Limpar qualquer erro anterior no localStorage
    localStorage.removeItem(LOGIN_ERROR_KEY);
    
    try {
      console.log('[DEBUG] Enviando credenciais:', { email: data.email });
      const result = await login(data.email, data.password);
      console.log('[DEBUG] Resultado do login:', result);
      
      if (result.success) {
        console.log('[DEBUG] Login bem-sucedido, redirecionando...');
        // Adicionando um pequeno delay para garantir que o estado seja atualizado antes da navegação
        setTimeout(() => {
          navigate('/');
        }, 100);
      } else {
        console.log('[DEBUG] Login falhou, exibindo erro:', result.error);
        // Exibir mensagem de erro retornada pela autenticação
        const errorMessage = result.error || 'Erro de autenticação';
        
        // Salvar o erro no localStorage para persistência
        localStorage.setItem(LOGIN_ERROR_KEY, errorMessage);
        console.log('[DEBUG] Erro salvo no localStorage');
        
        // Atualizar os estados
        setLoginError(errorMessage);
        setShowError(true);
        
        console.log('[DEBUG] Definindo loginError para:', errorMessage);
        console.log('[DEBUG] Definindo showError para: true');
        
        // Focar no campo de email após um pequeno delay
        setTimeout(() => {
          const emailField = document.getElementById('email');
          if (emailField) emailField.focus();
        }, 100);
      }
    } catch (error) {
      console.error('[DEBUG] Erro durante login:', error);
      const errorMessage = 'Ocorreu um erro durante o login. Tente novamente.';
      
      // Salvar o erro no localStorage para persistência
      localStorage.setItem(LOGIN_ERROR_KEY, errorMessage);
      console.log('[DEBUG] Erro salvo no localStorage');
      
      // Atualizar os estados
      setLoginError(errorMessage);
      setShowError(true);
      
      console.log('[DEBUG] Definindo loginError para:', errorMessage);
      console.log('[DEBUG] Definindo showError para: true');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h3 className="text-xl font-medium text-gray-900 mb-6 text-center">
        Acesse sua conta
      </h3>
      
      {/* Alerta de erro com estilo mais destacado */}
      {console.log('[DEBUG] Renderizando componente, loginError:', loginError, 'showError:', showError)}
      <div 
        className={`mb-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 rounded-md flex items-start ${showError ? 'block' : 'hidden'}`}
        style={{ display: showError ? 'flex' : 'none' }}
      >
        <FiAlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0 text-red-500" />
        <span className="font-medium">{loginError || ''}</span>
      </div>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="email" className="form-label">
            E-mail
          </label>
          <div className="relative mt-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiMail className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="email"
              type="email"
              className={`input pl-10 ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="seu@email.com"
              {...register('email', {
                required: 'E-mail é obrigatório',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'E-mail inválido'
                }
              })}
            />
          </div>
          {errors.email && (
            <p className="form-error">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="form-label">
            Senha
          </label>
          <div className="relative mt-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiLock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="password"
              type="password"
              className={`input pl-10 ${errors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="••••••••"
              {...register('password', {
                required: 'Senha é obrigatória',
                minLength: {
                  value: 6,
                  message: 'A senha deve ter pelo menos 6 caracteres'
                }
              })}
            />
          </div>
          {errors.password && (
            <p className="form-error">{errors.password.message}</p>
          )}
        </div>

        <div>
          <button
            type="submit"
            className="w-full btn btn-primary py-2.5"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Entrando...
              </span>
            ) : (
              'Entrar'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Login;
